/* EventDetail — public detail page for a single event.
 *
 * Renders cover image, rich detail content, time/location info, organiser
 * contact, gallery, and an embedded map when coordinates are present.
 *
 * Admins (super_admin / province_admin / editor) see an "Edit Modu" toggle
 * that unlocks inline editing of title, description, long content, dates,
 * venue/address, organiser contact, province — and a Facebook-style
 * repositionable cover image (drag + X/Y stretch sliders) matching the
 * NewsDetail editor. Changes are persisted via PUT /api/events/{id}.
 */
/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Calendar, Clock, MapPin, Phone, User as UserI, Globe, Share2,
  Facebook, Twitter, Send, Instagram, Bell, BellRing,
  Save, ImagePlus, Move, ZoomIn,
} from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import RichContent from "@/components/RichContent";
import { useAuth } from "@/contexts/AuthContext";
import MediaPicker from "@/components/admin/MediaPicker";
import { shareStoryImage } from "@/lib/share";
import StoryPreviewButton from "@/components/StoryPreviewButton";

export default function EventDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [favCount, setFavCount] = useState(0);
  const [favBusy, setFavBusy] = useState(false);
  const navigate = useNavigate();

  // Inline edit state (admin only)
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const dragRef = useRef(null);
  const canEdit = user && ["super_admin", "province_admin", "editor"].includes(user?.role);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/events/${id}`)
      .then((r) => {
        if (cancelled) return;
        setEvent(r.data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
        setLoading(false);
      });
    api.get(`/events/${id}/favorite-status`)
      .then((r) => {
        if (cancelled) return;
        setFavorited(!!r.data.favorited);
        setFavCount(r.data.count || 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  // Lazy-load provinces only when the admin flips into edit mode so public
  // readers don't pay for an unused API call.
  useEffect(() => {
    if (!editMode || provinces.length) return;
    let cancelled = false;
    api.get("/provinces?active_only=false").then((r) => {
      if (!cancelled) setProvinces(r.data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [editMode, provinces.length]);

  const toggleFavorite = async () => {
    if (!user) {
      toast.info("Hatırlatma kurmak için giriş yapın");
      navigate("/giris");
      return;
    }
    setFavBusy(true);
    try {
      const r = await api.post(`/events/${id}/favorite`);
      setFavorited(!!r.data.favorited);
      setFavCount((c) => c + (r.data.favorited ? 1 : -1));
      toast.success(r.data.favorited
        ? "Hatırlatma kuruldu. Başlamadan ~2 saat önce bildireceğiz."
        : "Hatırlatma iptal edildi"
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    } finally {
      setFavBusy(false);
    }
  };

  // ---------------------------------------------------------------------
  // Inline editor plumbing
  // ---------------------------------------------------------------------
  const toLocalInput = (iso) => {
    if (!iso) return "";
    // datetime-local inputs want "YYYY-MM-DDTHH:mm" in local time.
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return "";
    }
  };

  const enterEdit = () => {
    const baseScale = typeof event.cover_scale === "number" ? event.cover_scale : 1;
    setDraft({
      title: event.title || "",
      description: event.description || "",
      content_html: event.content_html || "",
      cover_image: event.cover_image || "",
      cover_focus_x: typeof event.cover_focus_x === "number" ? event.cover_focus_x : 50,
      cover_focus_y: typeof event.cover_focus_y === "number" ? event.cover_focus_y : 50,
      cover_scale: baseScale,
      cover_scale_x: typeof event.cover_scale_x === "number" ? event.cover_scale_x : baseScale,
      cover_scale_y: typeof event.cover_scale_y === "number" ? event.cover_scale_y : baseScale,
      start_at: toLocalInput(event.start_at),
      end_at: toLocalInput(event.end_at),
      venue: event.venue || "",
      address: event.address || "",
      province_id: event.province_id || "",
      district: event.district || "",
      event_type: event.event_type || "",
      contact_name: event.contact_name || "",
      contact_phone: event.contact_phone || "",
      website: event.website || "",
      cover_link_axes: false,
    });
    setEditMode(true);
  };

  const cancelEdit = () => { setEditMode(false); setDraft(null); setShowMediaPicker(false); };

  const saveEdit = async () => {
    if (!draft) return;
    if (!draft.title?.trim()) { toast.error("Başlık boş olamaz"); return; }
    if (!draft.start_at) { toast.error("Başlangıç tarihi zorunlu"); return; }
    setSaving(true);
    try {
      // Strip client-only fields; coerce datetime-locals back to ISO strings.
      const { cover_link_axes, ...rest } = draft;
      const payload = {
        ...rest,
        province_id: draft.province_id || null,
        start_at: new Date(draft.start_at).toISOString(),
        end_at: draft.end_at ? new Date(draft.end_at).toISOString() : null,
      };
      const r = await api.put(`/events/${id}`, payload);
      setEvent(r.data);
      setEditMode(false);
      setDraft(null);
      toast.success("Değişiklikler kaydedildi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  // Facebook-style drag-to-reposition on the cover image.
  const onCoverDown = (e) => {
    if (!editMode) return;
    e.preventDefault();
    const box = e.currentTarget.getBoundingClientRect();
    const startX = e.clientX ?? e.touches?.[0]?.clientX;
    const startY = e.clientY ?? e.touches?.[0]?.clientY;
    const startFx = draft.cover_focus_x;
    const startFy = draft.cover_focus_y;
    dragRef.current = { box, startX, startY, startFx, startFy };
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const cx = ev.clientX ?? ev.touches?.[0]?.clientX;
      const cy = ev.clientY ?? ev.touches?.[0]?.clientY;
      const dx = ((cx - d.startX) / d.box.width) * 100;
      const dy = ((cy - d.startY) / d.box.height) * 100;
      setDraft((s) => ({
        ...s,
        cover_focus_x: Math.max(0, Math.min(100, d.startFx - dx)),
        cover_focus_y: Math.max(0, Math.min(100, d.startFy - dy)),
      }));
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move);
    window.addEventListener("touchend", up);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-72 bg-gray-100 rounded-lg animate-pulse" />
        <div className="h-8 bg-gray-100 rounded mt-4 w-2/3 animate-pulse" />
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <h1 className="text-xl font-bold">Etkinlik bulunamadı</h1>
        <p className="text-sm text-gray-500 mt-1">
          Aradığınız etkinlik kaldırılmış veya süresi geçmiş olabilir.
        </p>
        <Link
          to="/etkinlikler"
          className="inline-block mt-4 px-4 py-2 text-sm bg-[#e11d2a] text-white rounded-md hover:bg-[#c01825]"
        >
          Tüm Etkinlikler
        </Link>
      </div>
    );
  }

  // Merged view used for displaying non-editable sections — in edit mode the
  // draft takes precedence so the user sees their changes immediately.
  const view = editMode && draft ? { ...event, ...draft } : event;

  const start = view.start_at ? new Date(view.start_at) : null;
  const end = view.end_at ? new Date(view.end_at) : null;
  const dateStr = start ? start.toLocaleString("tr-TR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }) : "—";
  const timeStr = start ? start.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : "";
  const endTimeStr = end ? end.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : "";

  const fullUrl = `${window.location.origin}/etkinlikler/${event.id}`;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const shareUrl = `${backendUrl}/api/og/event/${event.id}`;
  const storyUrl = `${backendUrl}/api/story/event/${event.id}`;
  const enc = encodeURIComponent;

  const shareInstagram = async () => {
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    if (isMobile && navigator.share) {
      try {
        await navigator.share({ title: event.title, text: event.description || event.title, url: fullUrl });
        return;
      } catch (e) { if (e?.name === "AbortError") return; }
    }
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link kopyalandı. Instagram açılıyor.", { duration: 5000 });
    } catch { /* clipboard might be blocked */ }
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };

  // Cover transform (same semantics as NewsDetail — scale_x/scale_y when
  // present override legacy uniform cover_scale).
  const buildTransform = (fx, fy, sx, sy) => ({
    objectPosition: `${fx}% ${fy}%`,
    transform: `scale(${sx}, ${sy})`,
    transformOrigin: `${fx}% ${fy}%`,
  });
  const coverStyle = editMode
    ? buildTransform(
        draft.cover_focus_x,
        draft.cover_focus_y,
        draft.cover_scale_x ?? draft.cover_scale ?? 1,
        draft.cover_scale_y ?? draft.cover_scale ?? 1,
      )
    : (
        typeof event.cover_focus_x === "number" ||
        typeof event.cover_scale_x === "number" ||
        typeof event.cover_scale_y === "number" ||
        event.cover_scale
      )
      ? buildTransform(
          event.cover_focus_x ?? 50,
          event.cover_focus_y ?? 50,
          event.cover_scale_x ?? event.cover_scale ?? 1,
          event.cover_scale_y ?? event.cover_scale ?? 1,
        )
      : undefined;

  return (
    <article className="max-w-4xl mx-auto px-4 py-6 relative" data-testid="event-detail">
      {/* Admin edit toolbar */}
      {canEdit && (
        <div
          className="sticky top-2 z-40 flex justify-end mb-2"
          data-testid="event-edit-controls"
        >
          <div className="bg-white shadow-lg border border-gray-200 rounded-full px-3 py-1.5 flex items-center gap-3 text-xs">
            <span className="font-semibold text-gray-600">Edit Modu</span>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={editMode}
                onChange={(e) => {
                  if (e.target.checked) enterEdit();
                  else saveEdit();
                }}
                data-testid="event-edit-toggle"
              />
              <span className="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-[#e11d2a] transition-colors relative before:content-[''] before:absolute before:w-4 before:h-4 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 peer-checked:before:translate-x-4 before:transition-transform" />
            </label>
            {editMode && (
              <>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  data-testid="event-edit-save"
                  className="text-emerald-600 hover:underline inline-flex items-center gap-1"
                >
                  <Save className="w-3 h-3" /> {saving ? "..." : "Kaydet"}
                </button>
                <button
                  onClick={cancelEdit}
                  data-testid="event-edit-cancel"
                  className="text-gray-500 hover:underline"
                >
                  İptal
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Cover image — repositionable in edit mode */}
      {(view.cover_image || editMode) && (
        <div
          className={`relative rounded-lg overflow-hidden mb-6 bg-gray-100 ${editMode ? "cursor-move ring-2 ring-amber-400" : ""}`}
          data-testid="event-cover-wrapper"
          onMouseDown={editMode ? onCoverDown : undefined}
          onTouchStart={editMode ? onCoverDown : undefined}
        >
          {view.cover_image ? (
            <img
              src={view.cover_image}
              alt={view.title}
              className="w-full h-auto max-h-[460px] object-cover select-none"
              draggable={false}
              style={coverStyle}
            />
          ) : (
            <div className="w-full h-72 flex items-center justify-center text-gray-400 text-sm">Kapak yok</div>
          )}
          {editMode && (
            <div className="absolute inset-x-0 bottom-0 bg-black/70 text-white p-2 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setShowMediaPicker((s) => !s)}
                data-testid="event-edit-change-photo"
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-white/20 hover:bg-white/30"
              >
                <ImagePlus className="w-3 h-3" /> Fotoğraf Değiştir
              </button>
              <span className="text-[11px] inline-flex items-center gap-1"><Move className="w-3 h-3" /> Sürükleyerek konumlandır</span>
              <label className="inline-flex items-center gap-2 text-[11px]">
                <ZoomIn className="w-3 h-3" /> X
                <input
                  type="range" min="0.5" max="3" step="0.05"
                  value={draft.cover_scale_x ?? 1}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setDraft({
                      ...draft,
                      cover_scale_x: v,
                      cover_scale_y: draft.cover_link_axes ? v : (draft.cover_scale_y ?? 1),
                      cover_scale: draft.cover_link_axes ? v : draft.cover_scale,
                    });
                  }}
                  className="w-24 accent-[#e11d2a]"
                  data-testid="event-edit-scale-x"
                />
                <span className="tabular-nums w-9">{Number(draft.cover_scale_x ?? 1).toFixed(2)}x</span>
              </label>
              <label className="inline-flex items-center gap-2 text-[11px]">
                <ZoomIn className="w-3 h-3 rotate-90" /> Y
                <input
                  type="range" min="0.5" max="3" step="0.05"
                  value={draft.cover_scale_y ?? 1}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setDraft({
                      ...draft,
                      cover_scale_y: v,
                      cover_scale_x: draft.cover_link_axes ? v : (draft.cover_scale_x ?? 1),
                      cover_scale: draft.cover_link_axes ? v : draft.cover_scale,
                    });
                  }}
                  className="w-24 accent-[#e11d2a]"
                  data-testid="event-edit-scale-y"
                />
                <span className="tabular-nums w-9">{Number(draft.cover_scale_y ?? 1).toFixed(2)}x</span>
              </label>
              <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!draft.cover_link_axes}
                  onChange={(e) => setDraft({ ...draft, cover_link_axes: e.target.checked })}
                  className="w-3 h-3 accent-[#e11d2a]"
                  data-testid="event-edit-link-axes"
                />
                Eksenleri Bağla
              </label>
              <button
                type="button"
                onClick={() => setDraft({
                  ...draft,
                  cover_focus_x: 50, cover_focus_y: 50,
                  cover_scale: 1, cover_scale_x: 1, cover_scale_y: 1,
                })}
                className="text-[11px] underline"
              >
                Sıfırla
              </button>
            </div>
          )}
          {editMode && showMediaPicker && (
            <div className="absolute inset-0 bg-white/95 p-4 overflow-auto z-10" data-testid="event-edit-media-picker">
              <div className="max-w-md mx-auto">
                <MediaPicker
                  value={draft.cover_image}
                  onChange={(u) => { setDraft({ ...draft, cover_image: u }); setShowMediaPicker(false); }}
                  scope={draft.province_id ? "local" : "shared"}
                  province_id={draft.province_id || null}
                  testId="event-edit-cover-picker"
                />
                <button
                  type="button"
                  onClick={() => setShowMediaPicker(false)}
                  className="mt-3 text-xs text-gray-500 hover:underline"
                >
                  Kapat
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {view.event_type && (
          <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-700 px-2 py-1 rounded">
            {view.event_type}
          </span>
        )}
        {view.is_featured && (
          <span className="text-[10px] uppercase tracking-wider bg-[#e11d2a]/10 text-[#e11d2a] px-2 py-1 rounded font-bold">
            Öne Çıkan
          </span>
        )}
      </div>

      {/* Title */}
      {editMode ? (
        <input
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          className="w-full text-2xl md:text-3xl font-black leading-tight bg-yellow-50 border border-dashed border-amber-300 rounded px-2 py-1 outline-none"
          data-testid="event-edit-title"
        />
      ) : (
        <h1 className="text-2xl md:text-3xl font-black leading-tight" data-testid="event-title">
          {view.title}
        </h1>
      )}

      {/* Favourite / reminder button — hidden in edit mode to reduce noise */}
      {!editMode && (
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={toggleFavorite}
            disabled={favBusy}
            data-testid="event-favorite-btn"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all shadow-sm ${
              favorited
                ? "bg-[#e11d2a] hover:bg-[#c01825] text-white"
                : "bg-white border border-gray-200 hover:border-[#e11d2a] hover:text-[#e11d2a] text-gray-800"
            } disabled:opacity-50`}
          >
            {favorited ? <BellRing className="w-4 h-4 animate-pulse" /> : <Bell className="w-4 h-4" />}
            {favorited ? "Hatırlatma Kurdun" : "Hatırlat Beni"}
          </button>
          {favCount > 0 && (
            <span className="text-xs text-gray-500" data-testid="event-favorite-count">
              {favCount} kişi hatırlatma kurdu
            </span>
          )}
          {favorited && (
            <span className="text-[11px] text-gray-500 inline-flex items-center gap-1">
              <BellRing className="w-3 h-3 text-[#e11d2a]" />
              Başlamadan ~2 saat önce bildireceğiz
            </span>
          )}
        </div>
      )}

      {/* Info card */}
      {editMode ? (
        <div className="mt-5 grid md:grid-cols-2 gap-3 bg-white border border-amber-200 rounded-lg p-4" data-testid="event-edit-infocard">
          <EditField label="Başlangıç *" type="datetime-local" value={draft.start_at}
            onChange={(v) => setDraft({ ...draft, start_at: v })} testId="event-edit-start-at" />
          <EditField label="Bitiş" type="datetime-local" value={draft.end_at}
            onChange={(v) => setDraft({ ...draft, end_at: v })} testId="event-edit-end-at" />
          <EditField label="Mekan" value={draft.venue}
            onChange={(v) => setDraft({ ...draft, venue: v })} testId="event-edit-venue" />
          <EditField label="Adres" value={draft.address}
            onChange={(v) => setDraft({ ...draft, address: v })} testId="event-edit-address" />
          <EditField label="İletişim (ad)" value={draft.contact_name}
            onChange={(v) => setDraft({ ...draft, contact_name: v })} testId="event-edit-contact-name" />
          <EditField label="Telefon" value={draft.contact_phone}
            onChange={(v) => setDraft({ ...draft, contact_phone: v })} testId="event-edit-contact-phone" />
          <EditField label="Web" value={draft.website}
            onChange={(v) => setDraft({ ...draft, website: v })} testId="event-edit-website" />
          <EditField label="Tip" value={draft.event_type}
            onChange={(v) => setDraft({ ...draft, event_type: v })} testId="event-edit-type" />
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">İl</label>
            <select
              value={draft.province_id || ""}
              onChange={(e) => setDraft({ ...draft, province_id: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded bg-white"
              data-testid="event-edit-province"
            >
              <option value="">Genel</option>
              {provinces.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <EditField label="İlçe" value={draft.district}
            onChange={(v) => setDraft({ ...draft, district: v })} testId="event-edit-district" />
        </div>
      ) : (
        <div className="mt-5 grid md:grid-cols-2 gap-3 bg-white border border-gray-100 rounded-lg p-4">
          <InfoRow icon={<Calendar className="w-4 h-4" />} label="Tarih" value={dateStr} />
          <InfoRow
            icon={<Clock className="w-4 h-4" />}
            label="Saat"
            value={timeStr ? (endTimeStr ? `${timeStr} - ${endTimeStr}` : timeStr) : "—"}
          />
          {view.venue && <InfoRow icon={<MapPin className="w-4 h-4" />} label="Mekan" value={view.venue} />}
          {view.address && <InfoRow icon={<MapPin className="w-4 h-4" />} label="Adres" value={view.address} />}
          {view.contact_name && (
            <InfoRow icon={<UserI className="w-4 h-4" />} label="İletişim" value={view.contact_name} />
          )}
          {view.contact_phone && (
            <InfoRow
              icon={<Phone className="w-4 h-4" />}
              label="Telefon"
              value={
                <a href={`tel:${view.contact_phone.replace(/\s/g, "")}`} className="hover:text-[#e11d2a]">
                  {view.contact_phone}
                </a>
              }
            />
          )}
          {view.website && (
            <InfoRow
              icon={<Globe className="w-4 h-4" />}
              label="Web"
              value={
                <a href={view.website} target="_blank" rel="noreferrer" className="hover:text-[#e11d2a] truncate inline-block max-w-[200px]">
                  {view.website}
                </a>
              }
            />
          )}
        </div>
      )}

      {/* Description */}
      {editMode ? (
        <textarea
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          rows={3}
          placeholder="Kısa açıklama (kart önizlemelerinde görünür)"
          className="w-full mt-5 text-base text-gray-800 leading-relaxed bg-yellow-50 border border-dashed border-amber-300 rounded px-3 py-2 outline-none"
          data-testid="event-edit-description"
        />
      ) : (
        view.description && (
          <p className="mt-5 text-base text-gray-700 whitespace-pre-wrap leading-relaxed" data-testid="event-description">
            {view.description}
          </p>
        )
      )}

      {/* Detail content */}
      {editMode ? (
        <textarea
          value={draft.content_html}
          onChange={(e) => setDraft({ ...draft, content_html: e.target.value })}
          rows={10}
          placeholder="Detaylı içerik (uzun açıklama, program, biletleme vb.) — YouTube/Vimeo linkini kendi satırına yapıştırabilirsiniz."
          className="w-full mt-4 text-base text-gray-800 leading-relaxed bg-yellow-50 border border-dashed border-amber-300 rounded px-3 py-2 outline-none font-mono"
          data-testid="event-edit-content"
        />
      ) : (
        view.content_html && (
          <RichContent
            text={view.content_html}
            className="mt-5 prose max-w-none text-base text-gray-800 leading-relaxed space-y-4"
          />
        )
      )}

      {/* Gallery (view-only; manage from admin panel) */}
      {!editMode && event.gallery && event.gallery.length > 0 && (
        <div className="mt-6">
          <h2 className="text-lg font-bold mb-2">Galeri</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {event.gallery.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-100 hover:opacity-90 transition">
                <img src={url} alt={`${event.title} ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Map */}
      {!editMode && event.lat != null && event.lng != null && (
        <div className="mt-6">
          <h2 className="text-lg font-bold mb-2 flex items-center gap-1">
            <MapPin className="w-4 h-4 text-[#e11d2a]" /> Konum
          </h2>
          <div className="border border-gray-100 rounded-lg overflow-hidden">
            <MapContainer center={[event.lat, event.lng]} zoom={15} style={{ height: 320, width: "100%" }} scrollWheelZoom={false}>
              <TileLayer attribution="© OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={[event.lat, event.lng]}>
                <Popup>{event.venue || event.title}</Popup>
              </Marker>
            </MapContainer>
          </div>
          <a
            href={`https://www.google.com/maps?q=${event.lat},${event.lng}`}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-[#e11d2a] hover:underline mt-2 inline-block"
          >
            Google Haritalar'da aç →
          </a>
        </div>
      )}

      {/* Share */}
      {!editMode && (
        <div className="flex items-center gap-2 mt-7 border-t border-b py-4">
          <span className="text-sm font-semibold flex items-center gap-1"><Share2 className="w-4 h-4" /> Paylaş:</span>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`} target="_blank" rel="noreferrer" data-testid="event-share-facebook" className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full"><Facebook className="w-4 h-4" /></a>
          <a href={`https://twitter.com/intent/tweet?text=${enc(event.title)}&url=${enc(shareUrl)}`} target="_blank" rel="noreferrer" data-testid="event-share-twitter" className="p-2 bg-black hover:bg-gray-800 text-white rounded-full"><Twitter className="w-4 h-4" /></a>
          <a href={`https://wa.me/?text=${enc(event.title)} ${enc(shareUrl)}`} target="_blank" rel="noreferrer" data-testid="event-share-whatsapp" className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-full"><Send className="w-4 h-4" /></a>
          <a href={`https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(event.title)}`} target="_blank" rel="noreferrer" data-testid="event-share-telegram" className="p-2 bg-sky-500 hover:bg-sky-600 text-white rounded-full"><Send className="w-4 h-4" /></a>
          <StoryPreviewButton
            url={storyUrl}
            filename={`sine81-etkinlik-${event.id}.png`}
            title={event.title}
            text={`${event.title} — SİNE81`}
            testIdShare="event-share-story"
            testIdPreview="event-share-story-preview"
          />
          <button
            type="button"
            onClick={shareInstagram}
            data-testid="event-share-instagram"
            className="p-2 text-white rounded-full hover:opacity-90"
            style={{ background: "radial-gradient(circle at 30% 110%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)" }}
          >
            <Instagram className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="mt-6">
        <Link to="/etkinlikler" className="text-sm text-[#e11d2a] hover:underline">
          ← Tüm Etkinlikler
        </Link>
      </div>
    </article>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="text-[#e11d2a] mt-0.5">{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
        <div className="text-gray-800 break-words">{value}</div>
      </div>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#e11d2a]/30 focus:border-[#e11d2a]"
        data-testid={testId}
      />
    </div>
  );
}
